import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ErroApi } from '../lib/api.ts';
import { dataBr, formatarTelefone } from '../lib/formato.ts';
import { copiar, linkWhatsapp, prepararTelefone } from '../lib/whatsapp.ts';
import { Aviso, Botao } from './forma.tsx';
import './avisarWhatsapp.css';

/** Marca do WhatsApp. Desenhada aqui em vez de vir de uma biblioteca de
 *  ícones, como a seta do painel do agente: são dois ícones no sistema inteiro,
 *  e um pacote de mil sairia mais caro que estas linhas.
 *
 *  `currentColor` porque ele aparece em botão verde com texto branco e, quando
 *  o envio está travado, dentro de um botão apagado -- seguir a cor do texto
 *  faz as duas situações sem um segundo desenho. */
function MarcaWhatsapp() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );
}

interface DadosAviso {
  codigo: number;
  paciente: string;
  telefone: string | null;
  aceitaWhatsapp: boolean;
  pronto: boolean;
  motivo: string | null;
  avisadoEm: string | null;
  mensagem: string;
  semSite: boolean;
}

/** Botão de avisar o paciente pelo WhatsApp.
 *
 *  Mostra a mensagem antes de abrir. Parece um passo a mais, mas é o único
 *  momento em que alguém lê o que vai sair — e é onde se percebe que o texto
 *  ficou com um campo não substituído ou que o número está estranho. */
export function AvisarWhatsapp({ exameId }: { exameId: string }) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const dados = useQuery({
    queryKey: ['aviso', exameId],
    queryFn: ({ signal }) => api.get<DadosAviso>(`/aviso/paciente/${exameId}`, signal),
    enabled: aberto,
  });

  const marcar = useMutation({
    mutationFn: () => api.post(`/aviso/paciente/${exameId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['aviso', exameId] });
      void qc.invalidateQueries({ queryKey: ['exames'] });
    },
  });

  if (!aberto) {
    return (
      <Botao variante="whatsapp" onClick={() => setAberto(true)}>
        <MarcaWhatsapp />
        Avisar pelo WhatsApp
      </Botao>
    );
  }

  const d = dados.data;
  const tel = prepararTelefone(d?.telefone);

  /* Campo que sobrou por substituir. Aconteceu de verdade: o banco recebeu um
     texto novo antes de a API que o entende subir, e a mensagem saiu com
     "Baixe aqui: {{pdf}}" para o paciente. O texto e o codigo que o preenche
     mudam por caminhos diferentes -- migration e deploy --, e nada garantia que
     chegassem juntos.

     Trava aqui em vez de limpar sozinho: apagar o campo deixaria "Baixe aqui:"
     apontando para o nada, que e pior, porque parece certo. */
  const sobrouCampo = /\{\{\s*[\w.]+\s*\}\}/.exec(d?.mensagem ?? '')?.[0];

  const impedimento = !d
    ? null
    : !d.pronto
      ? d.motivo
      : !d.aceitaWhatsapp
        ? 'Este paciente não autorizou receber mensagens. Confira o cadastro antes.'
        : d.semSite
          ? 'Falta cadastrar o endereço do site em Configurações — sem ele o link sai quebrado.'
          : sobrouCampo
            ? `A mensagem saiu com ${sobrouCampo} sem preencher. Ou o campo não existe ` +
              '(confira a lista em Configurações), ou o sistema ainda não foi atualizado. ' +
              'Não envie assim.'
            : tel.problema
              ? `Não dá para montar o link: ${tel.problema}.`
              : null;

  return (
    <div className="avisar cartao">
      <header className="avisar__topo">
        <p className="rotulo">Avisar o paciente</p>
        <button className="avisar__fechar" onClick={() => setAberto(false)} aria-label="Fechar">
          ×
        </button>
      </header>

      {dados.isPending ? (
        <p className="vazio">Carregando…</p>
      ) : dados.error ? (
        <Aviso>{dados.error instanceof ErroApi ? dados.error.message : 'Não foi possível carregar.'}</Aviso>
      ) : (
        <div className="avisar__corpo">
          <p className="avisar__para">
            <strong>{d!.paciente}</strong>
            <span className="mono"> · {formatarTelefone(d!.telefone) || 'sem telefone'}</span>
          </p>

          {d!.avisadoEm && (
            <p className="avisar__ja">
              Já foi aberto um aviso em {dataBr(d!.avisadoEm.slice(0, 10))}. Enviar de novo não
              é problema.
            </p>
          )}

          {impedimento && <Aviso>{impedimento}</Aviso>}

          <pre className="avisar__mensagem">{d!.mensagem}</pre>

          <div className="avisar__acoes">
            <a
              className={`botao botao--whatsapp${impedimento ? ' botao--travado' : ''}`}
              href={impedimento ? undefined : linkWhatsapp(tel.internacional!, d!.mensagem)}
              target="_blank"
              rel="noreferrer"
              aria-disabled={impedimento ? true : undefined}
              onClick={(e) => {
                if (impedimento) {
                  e.preventDefault();
                  return;
                }
                marcar.mutate();
              }}
            >
              <MarcaWhatsapp />
              Abrir o WhatsApp
            </a>
            <Botao
              variante="neutro"
              onClick={() => {
                void copiar(d!.mensagem).then(setCopiado);
              }}
            >
              {copiado ? 'Copiado' : 'Copiar mensagem'}
            </Botao>
          </div>

          <p className="campo__dica">
            O WhatsApp abre com o texto pronto — conferir o número e apertar enviar é com você.
            O sistema registra que o aviso foi aberto, não que a mensagem chegou.
          </p>
        </div>
      )}
    </div>
  );
}
